<template>
  <div class="exercise-cards">
    <div class="exercise-cards-main">
      <transition mode="out-in" :name="activeExerciseIndex > 1 && direction === 'right' ? 'slide-fade-list-right' : ''">
        <stacked-card :key="activeExerciseIndex">
          <div slot="header">
            <paragraph
              v-if="exercises.length > 0"
              class="effort-text"
              :type="'p'"
              :variation="'label'"
            >
              Effort
            </paragraph>
            <div :class="['effort-label', effortColor]">
              {{ effortType.toUpperCase() }}
            </div>
            <div v-if="isManualAssurance" class="manual-assurance-card">
              <span>MANUAL ASSURANCE</span>
            </div>
          </div>
          <div slot="body">
            <div v-if="exercises.length > 0" class="stacked-card-body-title">
              <paragraph :type="'p'">
                {{ exercises[activeExerciseIndex].title }}
              </paragraph>
            </div>
            <custom-button
              v-if="exercises.length > 0"
              @click="openDetails(activeExerciseIndex)"
              :text="'Open details'"
              :size="'button-medium'"
              :type="'achromatic-dark'"
            ></custom-button>
            <div v-else class="stacked-card-body-spinner">
              <i v-if="isFetchingData" class="fa fa-spinner fa-pulse"></i>
              <paragraph v-else :type="'p'" class="stacked-card-body-empty-message" v-html="emptyMessage"></paragraph>
            </div>
          </div>
          <div slot="footer" class="stacked-card-footer" v-if="exercises.length > 0">
            <div class="hackability-share">
              <p>Hackability %</p>
              <p class="font-weight-bold">
                {{ exercises[activeExerciseIndex].hackability_share }}%
              </p>
            </div>
            <div class="number-of-hosts">
              <p># of hosts</p>
              <p class="font-weight-bold">
                {{ exercises[activeExerciseIndex].number_of_affected_hosts }}
              </p>
            </div>
          </div>
        </stacked-card>
      </transition>
      <transition :name="direction === 'right' && activeExerciseIndex !== 1 ? '' :
        activeExerciseIndex > 0 && direction === 'left' ? 'slide-fade-list-left' :
        'slide-fade-right'" v-if="activeExerciseIndex - 1 !== -1 && exercises.length !== 0"
      >
        <stacked-card :key="activeExerciseIndex">
          <div slot="header">
            <paragraph
              v-if="exercises.length > 0"
              class="effort-text"
              :type="'p'"
              :variation="'label'"
            >
              Effort
            </paragraph>
            <div :class="['effort-label', effortColor]">
              {{ effortType.toUpperCase() }}
            </div>
             <div v-if="isManualAssurance" class="manual-assurance-card">
              <span>MANUAL ASSURANCE</span>
            </div>
          </div>
          <div slot="body">
            <div v-if="exercises.length > 0" class="stacked-card-body-title">
              <paragraph :type="'p'">
                {{ exercises[activeExerciseIndex - 1].title }}
              </paragraph>
            </div>
            <custom-button
              v-if="exercises.length > 0"
              @click="openDetails(activeExerciseIndex - 1)"
              :text="'Open details'"
              :size="'button-medium'"
              :type="'achromatic-dark'"
            ></custom-button>
            <div v-else class="stacked-card-body-spinner">
              <i v-if="isFetchingData" class="fa fa-spinner fa-pulse"></i>
              <paragraph v-else :type="'p'" class="stacked-card-body-empty-message" v-html="emptyMessage"></paragraph>
            </div>
          </div>
          <div slot="footer" class="stacked-card-footer" v-if="exercises.length > 0">
            <div class="hackability-share">
              <p>Hackability %</p>
              <p class="font-weight-bold">
                {{ exercises[activeExerciseIndex - 1].hackability_share }}%
              </p>
            </div>
            <div class="number-of-hosts">
              <p># of hosts</p>
              <p class="font-weight-bold">
                {{ exercises[activeExerciseIndex - 1].number_of_affected_hosts }}
              </p>
            </div>
          </div>
        </stacked-card>
      </transition>
      <transition name="slide-fade-left" mode="out-in" v-else>
        <title-card>
          <div slot="header">
            <img
              class="autobahn-logo"
              src="/assets/images/Autobahn_vertical_combination_mark_black.png"
              alt="autobahn-logo-vertical"
            />
          </div>
          <div slot="body">
            <heading :level="'h3'">
              {{ titleBody }}
            </heading>
          </div>
          <div slot="footer">
            <paragraph :type="'p'" :variation="'small'">
              {{ titleFooter }}
            </paragraph>
          </div>
        </title-card>
      </transition>
    </div>
    <div class="exercise-cards-pagination">
      <div
        class="exercise-cards-pagination-item"
        :class="{ inactive: activeExerciseIndex === 0 || exercises.length === 0 }"
        @click="prevExercise"
      >
        <font-awesome-icon :icon="['far', 'angle-left']" size="lg" />
      </div>
      <div
        class="exercise-cards-pagination-item"
        :class="{ inactive: activeExerciseIndex === exercises.length - 1 || exercises.length === 0  || activeExerciseIndex + 2 > exercises.length}"
        @click="nextExercise"
      >
        <font-awesome-icon :icon="['far', 'angle-right']" size="lg" />
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CustomButton from '~/components/shared/custom-button.vue';
import Heading from '~/components/shared/heading.vue';
import Paragraph from '~/components/shared/paragraph.vue';
import StackedCard from './stacked-card.vue';
import TitleCard from './title-card.vue';

library.add(faAngleLeft);
library.add(faAngleRight);

export default {
    name: 'exercise-cards',
    components: {
        CustomButton,
        FontAwesomeIcon,
        Heading,
        Paragraph,
        StackedCard,
        TitleCard,
    },
    props: {
        effortColor: {
            type: String,
        },
        effortType: {
            type: String,
        },
        titleBody: {
            type: String,
        },
        titleFooter: {
            type: String,
        },
        exercises: {
            type: Array,
            default: () => [],
        },
        activeExerciseIndex: {
            type: Number,
            default: 0,
        },
        isFetchingData: {
            type: Boolean,
            default: true,
        },
        emptyMessage: {
            type: String,
        },
        isManualAssurance: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            direction: 'left',
        };
    },
    methods: {
        prevExercise() {
            this.$emit('prevExercise');
            this.direction = 'left';
        },
        nextExercise() {
            this.$emit('nextExercise');
            this.direction = 'right';
        },
        openDetails(index) {
            this.$emit('openDetails', index);
        },
    },
};
</script>

<style lang="scss">
.exercise-cards {
  &-main {
    display: flex;
    justify-content: center;
    padding-top: 1rem;
    padding-bottom: 1rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;

    .stacked-card,
    .title-card {
      min-height: clamp(
        19.2vw,
        calc((((100vw - 1440px) / 120) * 19.2) + 22.2vw),
        450px
      );
      max-width: 300px;
    }

    .stacked-card,
    .title-card {
      flex: 1;
      margin-left: 0.5rem;
      margin-right: 0.5rem;
    }

    .effort-label {
      margin-top: 0.2rem;
      margin-right: auto;
      margin-left: auto;
      padding: 5px 7px;
      color: white;
      border-radius: 7%;
      width: fit-content;
      font-weight: bold;
      font-size: 0.7rem;
      letter-spacing: 1px;

      &.green {
        background-color: $color-semantic-green-300
      }

      &.orange {
        background-color: $color-semantic-orange-300;
      }
    }
    .manual-assurance-card{
      margin-top: 0.5rem;
      margin-right: auto;
      margin-left: auto;
      padding: 5px 7px;
      color: white;
      border-radius: 2px;
      width: fit-content;
      font-weight: bold;
      font-size: 0.7rem;
      letter-spacing: 1px;
      background-color: darkgray;
    }
    .stacked-card {
      justify-content: space-between;
      background-color:white;

      &-header {
        .circular-text {
          margin: auto;
          margin-top: 0.4rem;
        }
      }

      &-body {
        padding-top: 1rem;
        padding-bottom: 1.5rem;

        &-title {
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding-bottom: 1rem;
          min-height: 11vw;

          .paragraph {
            font-weight: $font-weight-button-default;
          }
        }

        &-spinner {
          height: 17.6vw;
          display: flex;
          justify-content: center;
          align-items: center;

          .fa-spinner {
            font-size: 3vw;
            color: $color-neutral-dark-100;
          }
        }

        &-empty-message {
          &.paragraph {
            font-weight: $font-weight-button-default;
            padding: 0.5rem;
          }
        }
      }

      &-footer {
        display: flex;
        justify-content: space-between;
        width: 100%;

        p {
          font-size: 0.8rem;
          margin-bottom: 0rem;
        }
      }
    }

    .title-card {
      .autobahn-logo {
        width: 45%;
        height: auto;
      }

      &-body {
        margin-top: 0.5rem;
        margin-bottom: 0;
      }

      &-footer {
        .paragraph {
          color: $color-neutral-dark-200;
          font-weight: $font-weight-body-default;
        }
      }
    }
  }

  &-pagination {
    display: flex;
    justify-content: center;

    &-item {
      cursor: pointer;
      border: 1px solid $color-neutral-light-500;
      border-radius: 4px;
      margin-left: 0.5rem;
      margin-right: 0.5rem;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 2.46rem;
      height: 2.46rem;
      user-select: none;
      color: $color-neutral-dark-500;

      &.inactive {
        color: $color-neutral-light-400;
      }
    }
  }

  .slide-fade-right-enter-active {
    transition: all 0.25s ease-out;
  }
  .slide-fade-right-enter {
    transform: translateX(-105%);
  }
  .slide-fade-left-leave-active {
    transition: all 0.25s ease-out;
  }
  .slide-fade-left-leave {
    transform: translateX(0%);
  }
  .slide-fade-left-leave-to {
    transform: translateX(-105%);
  }

  .slide-fade-list-right {
    transition: all 0.25s ease-out;
  }
  .slide-fade-list-right-enter-active {
    transition: all 0.25s ease-out;
  }
  .slide-fade-list-right-enter {
    transform: translateX(0%);
  }
  .slide-fade-list-right-enter-to {
    transform: translateX(105%);
  }

  .slide-fade-list-left {
    transition: all 0.25s ease-out;
  }
  .slide-fade-list-left-enter-active {
    transition: all 0.25s ease-out;
  }
  .slide-fade-list-left-enter {
    transform: translateX(0%);
  }
  .slide-fade-list-left-enter-to {
    transform: translateX(-105%);
  }

}

</style>
